import { type ReactNode, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface FilterBarProps {
  search?: {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
  };
  filters?: ReactNode;
  className?: string;
}

export function FilterBar({ search, filters, className }: FilterBarProps) {
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2">
        {/* Mobile: search icon toggle */}
        {search && !searchOpen && (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-md border border-input hover:bg-accent transition-colors"
            aria-label="Open search"
          >
            <Search className="h-4 w-4 text-muted-foreground" />
          </button>
        )}

        {/* Mobile: expanded search */}
        {search && searchOpen && (
          <div className="flex flex-1 items-center gap-2 md:hidden">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder={search.placeholder}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={() => {
                search.onChange('');
                setSearchOpen(false);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent transition-colors"
              aria-label="Close search"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        )}

        {/* Desktop: always-visible search */}
        {search && (
          <div className="relative hidden md:block md:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder={search.placeholder}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {/* Desktop: inline filters */}
        {filters && (
          <div className="hidden md:flex items-center gap-2">
            {filters}
          </div>
        )}
      </div>

      {/* Mobile: filter chips row */}
      {filters && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none md:hidden">
          {filters}
        </div>
      )}
    </div>
  );
}
